import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, Building2, Briefcase, GitBranch,
    CheckSquare, Calendar as CalendarIcon, Shield, ScrollText,
    LogOut, Menu, ScanLine, IdCard, Share2, ClipboardCheck, BrainCircuit, ListChecks,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth } from "../context/AuthContext";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard" },
    { to: "/leads", label: "Contactos", icon: Users, perm: "contacts_view" },
    { to: "/companies", label: "Empresas", icon: Building2, perm: "companies_view" },
    { to: "/deals", label: "Deals", icon: Briefcase, perm: "deals_view" },
    { to: "/funnels", label: "Embudos", icon: GitBranch, perm: "funnels_view" },
    { to: "/tasks", label: "Tareas", icon: CheckSquare, perm: "tasks_view" },
    { to: "/calendar", label: "Calendario", icon: CalendarIcon, perm: "calendar_view" },
    { to: "/service-audit", label: "Auditoría de servicios", icon: ClipboardCheck, permAny: ["service_financial_view", "service_weekly_audit_view", "service_backups_view", "code_security_view"] },
    { to: "/weekly-control", label: "Tráfico semanal", icon: ListChecks, perm: "weekly_control_view" },
    { to: "/ai-usage", label: "Consumo IA", icon: BrainCircuit, perm: "ai_usage" },
    { to: "/scan-card", label: "Escanear tarjeta", icon: ScanLine, perm: "contacts_manage" },
    { to: "/my-card", label: "Mi tarjeta digital", icon: IdCard, perm: "dashboard" },
    { to: "/share-card", label: "Compartir mi tarjeta", icon: Share2, perm: "dashboard" },
];

export default function MobileDrawer() {
    const { user, logout, hasPermission, hasAnyPermission } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const visibleNav = NAV.filter((item) => item.permAny ? hasAnyPermission(item.permAny) : hasPermission(item.perm));

    const handleLogout = async () => {
        setOpen(false);
        await logout();
        navigate("/login");
    };

    const navItem = "flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors";

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <button
                    aria-label="Abrir menú de navegación"
                    data-testid="mobile-menu-button"
                    className="p-2 -ml-2 text-slate-200 hover:bg-slate-800 rounded-md transition-colors"
                >
                    <Menu className="w-6 h-6" />
                </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-slate-900 border-slate-800" data-testid="mobile-drawer">
                <VisuallyHidden>
                    <SheetTitle>Menú principal</SheetTitle>
                    <SheetDescription>Navegación entre los módulos del CRM</SheetDescription>
                </VisuallyHidden>
                <div className="flex flex-col h-full text-slate-300">
                    <div className="px-3 py-3 border-b border-slate-800 flex items-center justify-center">
                        <img src="/logo-emprenovus.png" alt="Emprenovus" className="h-14 w-auto object-contain" data-testid="drawer-logo" />
                    </div>
                    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin" role="navigation" aria-label="Menú principal">
                        {visibleNav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${item.perm}`}
                                className={({ isActive }) =>
                                    `${navItem} ${isActive ? "bg-orange-500 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
                                }
                            >
                                <item.icon className="w-5 h-5" />
                                {item.label}
                            </NavLink>
                        ))}
                        {hasPermission("admin") && (
                            <NavLink
                                to="/admin"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-nav-admin"
                                className={({ isActive }) => `${navItem} ${isActive ? "bg-orange-500 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                            >
                                <Shield className="w-5 h-5" />
                                Administración
                            </NavLink>
                        )}
                        {hasPermission("audit") && (
                            <NavLink
                                to="/audit"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-nav-audit"
                                className={({ isActive }) => `${navItem} ${isActive ? "bg-orange-500 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                            >
                                <ScrollText className="w-5 h-5" />
                                Auditoría
                            </NavLink>
                        )}
                    </nav>
                    <div className="border-t border-slate-800 p-4">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-medium">
                                {user?.name?.[0]?.toUpperCase() || "U"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-white font-medium truncate">{user?.name}</div>
                                <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="mobile-logout-button"
                            className="w-full flex items-center justify-center gap-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800 py-2 rounded-md transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
